import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { keyframes, Card, Group, RingProgress, useMantineTheme, Tooltip, ActionIcon, Space, Text, Loader, createStyles, Menu } from '@mantine/core';
import { useUserAuth } from "../../contexts/user.context";
import { IconDownload, IconUpload, IconRefresh } from "@tabler/icons-react";
import AliasFileUpload from '../../components/alias-file-upload/alias-file-upload.component';
import QualtricsFileUpload from '../../components/qualtrics-file-upload/qualtrics-file-upload.component';
import { useMediaQuery } from "@mantine/hooks";
import { beigeColor, flagsForModel, shortenedFlags } from "../../assets/constants";
import useSurveysProgress from '../../assets/custom-hooks/useSurveysProgress';
import LogoWithText from '../../components/logo-with-text/logo-with-text.component';
import { getDataForSurvey } from '../../utils/firebase/firebase.utils';
import Papa from 'papaparse';
import TableSorting from '../../components/table-sorting/table-sorting-component';

const rotate = keyframes({
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
});

const useStyles = createStyles((theme) => ({

    toolbar: {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '55px',
        borderBottom: '1px solid #f1f1f1',
        padding: '0px 20px',
    },

    innerPlaygroundToolbarDiv: {
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        [theme.fn.smallerThan('sm')]: {
            width: '100%',
        },
    },

    aliasWrapper: {
        display: 'flex',
        width: '100%',
        height: 'calc(100vh - 110px)',
    },

    surveyHistory: {
        width: '260px',
        borderRight: '1px solid #f1f1f1',
        overflowY: 'scroll',
        overflowX: 'hidden',
        flexGrow: 0,
    },

    surveyTitleContainer: {
        // Sticky
        position: 'sticky',
        top: 0,
        backgroundColor: 'white',
        zIndex: '100',
    },

    surveyHistoryTitle: {
        paddingLeft: '25px',
    },

    loaderListContainer: {
        marginTop: '15px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },

    loaderContainer: {
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
    },

    surveyId: {
        width: '100%',
        height: '50px',
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        paddingLeft: '25px',
        borderTop: '1px solid #f1f1f1',
        color: '#666',
        position: 'relative',
        whiteSpace: 'nowrap',
        '&:hover': {
            backgroundColor: theme.colors.gray[2],
            color: 'black',
        },
    },

    surveyIdText: {
        width: '100%',
        maskImage: `linear-gradient(to right, black 70%, transparent 95%)`,
        WebkitMaskImage: `linear-gradient(to right, black 70%, transparent 95%)`,
    },

    noSurveysFound: {
        paddingLeft: '25px',
        color: theme.colors.gray[6],
    },

    linkText: {
        color: theme.colors.gray[6],
        // hover
        '&:hover': {
            color: 'black',
        },
    },

    toolbarLinkText: {
        color: 'black',
        '&:hover': {
            textDecoration: 'underline',
        },

    },

    surveyIdSelected: {
        color: 'black',
        backgroundColor: theme.colors.gray[2],
        '&:hover': {
            backgroundColor: theme.colors.gray[2],
        },
    },

    surveyVisualizer: {
        width: 'calc(100% - 260px)',
        overflowX: 'hidden',
        overflowY: 'scroll',
        background: beigeColor,
        padding: '35px',
        flexGrow: 1,
    },

    toolbarIcon: {
        width: '20px',
        height: '20px',
        color: theme.colors.dark[7],
    },

    toolbarItemDisabled: {
        stroke: theme.colors.gray[5],
    },

    smallScreenContainer: {
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        padding: '20px',
    },

    plotWrapper: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
    },

    flexText: {
        display: 'flex',
        width: '100%',
        flexGrow: 1,
    },

    flexTextSpan: {
        display: 'flex',
        flex: 1,
        textAlign: 'right',

        // Before
        '&::before': {
            content: '""',
            borderBottom: `1.5px dotted ${theme.colors.gray[5]}`,
            flexGrow: 1,
            display: 'inline-block',
            height: '1px',
            transform: 'translateY(1.05em)',
            margin: '0 0.4em',
        }
    },

    rotating: {
        animation: `${rotate} 1.75s linear infinite`,
    },

    logoContainer: {
        width: '100%',
        height: 'calc(100vh - 180px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    }
}));



const getScrollbarWidth = () => {
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.overflow = 'scroll';
    document.body.appendChild(outer);
    const inner = document.createElement('div');
    outer.appendChild(inner);
    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
    document.body.removeChild(outer);
    return scrollbarWidth;
}

const getKeysWithChangeHistory = (keys, questions) => {
    const keysToFind = new Set(keys);
    const changedKeys = new Set();

    for (const question of questions) {
        if (question.changeHistory) {
            Object.keys(question.changeHistory).forEach(key => {
                if (keysToFind.has(key)) {
                    changedKeys.add(key);
                    keysToFind.delete(key);
                }
            });
        }

        // Stop if we've found all keys
        if (keysToFind.size === 0) break;
    }

    return Array.from(changedKeys);
}

const checkForFingerprint = (rawSurveyData) => {
    for (let i = 0; i < rawSurveyData.length; i++) {
        if (rawSurveyData[i]['fingerprintChecks']) {
            if (typeof rawSurveyData[i]['fingerprintChecks'] === 'object') {
                if (Object.keys(rawSurveyData[i]['fingerprintChecks']).length > 0) return true;
            }
        }
    }
    return false;
};

const getFingerprintRows = (aliasModel) => {
    const fpRows = [
        { 'columnName': 'Device ID', 'columnKey': 'alias-deviceID' },
        { 'columnName': 'Duplicate Device', 'columnKey': 'alias-duplicateDevice' },
    ];
    if (aliasModel === 'alias-v020') return fpRows;
    return [
        ...fpRows,
        { 'columnName': 'IP Country', 'columnKey': 'alias-ipCountry' },
        { 'columnName': 'Bot detected', 'columnKey': 'alias-bot' },
        { 'columnName': 'Using VPN', 'columnKey': 'alias-vpn' },
        { 'columnName': 'Using Incognito', 'columnKey': 'alias-incognito' },
        { 'columnName': 'Browser Tampering', 'columnKey': 'alias-tampering' },
        { 'columnName': 'Using Virtual Machine', 'columnKey': 'alias-virtualMachine' },
    ];
};


const getFpValue = (question,field) => {
    const rawValue = String(question?.fingerprintChecks?.[field]);
    if (rawValue) return rawValue;
    return 'Unknown';
};

const checkDuplicateFingerprintId = (device_id, surveyData) => {
    if (device_id === undefined) return 'Unknown';
    if (device_id === null) return 'Unknown';
    // Check if there's another row with the same fingerprint.participant_id
    // There should be at least two rows with the same fingerprint.participant_id
    const duplicate = surveyData.filter(row => row['fingerprintChecks']?.participant_id === device_id).length > 1;
    return String(duplicate);
};

const formatDataForRows = (rawSurveyData) => {
    const aliasModel = rawSurveyData[0]?.aliasModel ?? 'Unknown';
    // Get question keys
    const questionKeys = [];
    // Loop through rawSurveyData and find all question keys
    rawSurveyData.forEach((question) => {
        Object.keys(question['finalStates']).forEach((key) => {
            if (!questionKeys.includes(key)) questionKeys.push(key);
        })
    });
    const formattedRows = [];
    const rowNamesTemp = [{ 'columnName': 'Participant ID', 'columnKey': 'alias-participantID' }]
    const includeFingerprint = checkForFingerprint(rawSurveyData);
    if (includeFingerprint) {
        const fingerprintRowData = getFingerprintRows(aliasModel);
        rowNamesTemp.push(...fingerprintRowData);
    }
    let includeResponseGroups = false;
    let includeEffortRatings = false;
    // Set hasExtraData to value of last item in rawSurveyData
    const hasExtraData = rawSurveyData[rawSurveyData.length - 1]['hasExtraData'] === true;
    // Loop through rawSurveyData and create rows
    const questionsWithHistories = [];
    const keysWithChangeHistory = getKeysWithChangeHistory(questionKeys, rawSurveyData);
    rawSurveyData.forEach((question, i) => {
        const row = {
            'alias-participantID': question['participantId'],
            'alias-surveyID': question['surveyId'],
            'alias-model': question['aliasModel'] || 'Unknown',
        };
        if (includeFingerprint) {
            const device_id = question?.fingerprintChecks?.participant_id;
            row['alias-deviceID'] = device_id ?? 'Unknown';
            row['alias-duplicateDevice'] = checkDuplicateFingerprintId(device_id, rawSurveyData);
            if (aliasModel !== 'alias-v020'){
                row['alias-ipCountry'] = getFpValue(question,'ip_country');
                row['alias-bot'] = getFpValue(question,'bot');
                row['alias-vpn'] = getFpValue(question,'vpn');
                row['alias-incognito'] = getFpValue(question,'incognito');
                row['alias-tampering'] = getFpValue(question,'tampering');
                row['alias-virtualMachine'] = getFpValue(question,'virtual_machine');
            };
        };

        questionKeys.forEach((key) => {
            if (i === 0) {
                // Check if all change histories for given question are empty
                rowNamesTemp.push({ 'columnName': `${key} Question`, 'columnKey': `${key}-alias-question` })
                rowNamesTemp.push({ 'columnName': `${key} Response`, 'columnKey': `${key}-alias-response` })
                rowNamesTemp.push({ 'columnName': `${key} Flags`, 'columnKey': `${key}-alias-flags` })
                if (keysWithChangeHistory.includes(key)) {
                    rowNamesTemp.push({ 'columnName': `${key} Typing`, 'columnKey': `${key}-changeHistory` })
                    questionsWithHistories.push(key);
                };
                // Check if responseGroups is empty and if so add it
                if (question['responseGroups'][key] !== undefined) {
                    includeResponseGroups = true;
                    rowNamesTemp.push({ 'columnName': `${key} Group`, 'columnKey': `${key}-alias-group` });
                }
                if (question['effortRatings'][key] !== undefined) {
                    includeEffortRatings = true;
                    rowNamesTemp.push({ 'columnName': `${key} Effort`, 'columnKey': `${key}-effortRating` });
                }
            }
            row[`${key}-alias-question`] = question['questions'][key] ? question['questions'][key] : '';
            row[`${key}-alias-response`] = question['finalStates'][key] === undefined ? 'None' : question['finalStates'][key];
            row[`${key}-alias-flags`] = question['checksReturnedToUser'][key] === undefined ? 'None' : question['checksReturnedToUser']?.[key]?.length === 0 ? 'None' : (question['checksReturnedToUser']?.[key].join('; ') ?? '');
            if (includeResponseGroups) {
                row[`${key}-alias-group`] = question['responseGroups'][key] === undefined ? -1 : question['responseGroups'][key];
            }
            if (includeEffortRatings) {
                row[`${key}-effortRating`] = question['effortRatings'][key] === undefined ? -1 : question['effortRatings'][key];
            }
            if (questionsWithHistories.includes(key)) {
                // Remove first element if it exists
                row[`${key}-changeHistory`] = question['changeHistory'][key] === undefined ? [] : question['changeHistory'][key];
            }
        });
        const keysToIgnore = ['participantId', 'fingerprintChecks', 'duplicateFingerprintParticipantId', 'aliasModel', 'APICallID', 'checksReturnedToUser', 'questions', 'finalStates', 'responseGroups', 'extraData', 'isQualtrics', 'hasExtraData', 'surveyId', 'changeHistory', 'effortRatings'];
        if (i === 0 && hasExtraData) {
            // Add all items to row
            Object.keys(question).forEach((key) => {
                if (!keysToIgnore.includes(key)) {
                    rowNamesTemp.push({ 'columnName': key, 'columnKey': key });
                }
            });
        }
        if (hasExtraData) {
            // Add all items to row
            Object.keys(question).forEach((key) => {
                if (!keysToIgnore.includes(key)) {
                    row[key] = question[key];
                }
            });
        }
        row['alias-id'] = question['APICallID'];
        formattedRows.push(row);
    });
    return { formattedRows, rowNamesTemp, aliasModel };
}


const downloadCSV = (arrayOfObjects, fileName = 'data.csv') => {
    // Remove changeHistory from arrayOfObjects
    arrayOfObjects.forEach((row) => { Object.keys(row).forEach((key) => { if (key.includes('-changeHistory')) delete row[key] }) });
    const csvString = Papa.unparse(arrayOfObjects);
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const checkIfQuestionAnswered = (flagKey, rowData) => {
    const responseColumnName = flagKey.slice(0, -6) + '-response';
    const response = rowData[responseColumnName];
    if (response && response !== '') return true;
    return false;
}

const getResponseStatistics = (rawData, surveyModel) => {
    const flagCounts = {};
    let numResponses = 0;
    // Loop through rawData
    rawData.forEach((response) => {
        const flagKeys = Object.keys(response).filter(key => key.includes('-alias-flags'));
        flagKeys.forEach((key) => {
            const flagsList = response[key].split('; ');
            const questionAnswered = checkIfQuestionAnswered(key, response);
            if (questionAnswered) numResponses++;
            flagsList.forEach((flag) => {
                if (questionAnswered) flagCounts[flag] = flagCounts[flag] ? flagCounts[flag] + 1 : 1;
            });
        });
    });
    // Sort in descending order and normalize
    const orderedFlagCounts = {};
    Object.keys(flagCounts).sort((a, b) => flagCounts[b] - flagCounts[a]).forEach((key) => {
        orderedFlagCounts[key] = flagCounts[key] * 100 / numResponses;
    });
    // Add all elements of flagsForModel to end of orderedFlagCounts if they are not already in the object
    const flagsForSurvey = flagsForModel[surveyModel];
    if (flagsForSurvey) {
        flagsForSurvey.forEach((flag) => {
            if (!Object.keys(orderedFlagCounts).includes(flag)) {
                orderedFlagCounts[flag] = 0;
            }
        });
    }
    return { orderedFlagCounts, numResponses };
}

const getFingerprintStatistics = (rawData) => {
    let numFingerprint = 0;
    let numDuplicates = 0;
    rawData.forEach((response) => {
        if (response['alias-deviceID'] && response['alias-deviceID'] !== 'Unknown') numFingerprint++;
        if (response['alias-duplicateDevice'] === 'true') numDuplicates++;
    });
    const propDuplicate = numFingerprint === 0 ? 0 : (numDuplicates*100) / numFingerprint;
    return { numFingerprint, propDuplicate };
}

const getElapsedText = (elapsedMinutes) => {
    if (elapsedMinutes === 0) return 'Last refreshed just now';
    if (elapsedMinutes === 1) return 'Last refreshed 1 minute ago';
    return `Last refreshed ${elapsedMinutes} minutes ago`;
}

const getFlagCountText = (flagCount) => {
    if (flagCount === 0) return '0';
    if (flagCount < 0.5) return '<1';
    return Math.round(flagCount);
};

const getProportionFlagged = (flagCountForNone) => {
    if (flagCountForNone === 0) return '100';
    if (flagCountForNone < 0.5) return '>99';
    return `${Math.round(100 - flagCountForNone)}`;
}

const Dashboard = () => {

    const [isLoadingSurvey, setIsLoadingSurvey] = useState(false);
    const [selectedSurveyId, setSelectedSurveyId] = useState(null);
    const [surveyData, setSurveyData] = useState([]);
    const [rowNames, setRowNames] = useState([]);
    const [aliasModel, setAliasModel] = useState('Unknown');
    const [numFingerprintDevices, setNumFingerprintDevices] = useState(0);
    const [propDeviceDuplicates, setPropDeviceDuplicates] = useState(0);
    const [uploadModalOpened, setUploadModalOpened] = useState(false);
    const [qualtricsModalOpened, setQualtricsModalOpened] = useState(false);
    const [loadingSurveys, setLoadingSurveys] = useState([]);
    const [isDownloading, setIsDownloading] = useState(false);
    const [flagCounts, setFlagCounts] = useState({});
    const [numQuestionsAnswered, setNumQuestionsAnswered] = useState(0);
    const [lastRefreshed, setLastRefreshed] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [elapsedMinutes, setElapsedMinutes] = useState(0);
    const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });

    const { classes, cx } = useStyles();

    const { currentUser, APICreditsAvailable, setAPICreditsAvailable, aliasSurveyIds, setAliasSurveyIds, aliasSurveysLoaded } = useUserAuth();

    const surveysProgress = useSurveysProgress(currentUser.uid, loadingSurveys, setLoadingSurveys);

    const theme = useMantineTheme();
    const isSmall = useMediaQuery(`(max-width: ${theme.breakpoints.sm}px)`);
    const isMedium = useMediaQuery(`(max-width: ${theme.breakpoints.md}px)`);
    const isXl = useMediaQuery(`(min-width: ${theme.breakpoints.xl}px)`);


    const getSurveyData = async () => {
        try {
            const surveyData = await getDataForSurvey(currentUser, selectedSurveyId);
            // Console log size of surveyData JSON in MB
            const { formattedRows, rowNamesTemp, aliasModel } = formatDataForRows(surveyData);
            const { orderedFlagCounts, numResponses } = getResponseStatistics(formattedRows, aliasModel);
            const { numFingerprint, propDuplicate } = getFingerprintStatistics(formattedRows); 
            setFlagCounts(orderedFlagCounts);
            setRowNames(rowNamesTemp);
            setNumQuestionsAnswered(numResponses);
            setSurveyData(formattedRows);
            setAliasModel(aliasModel);
            setNumFingerprintDevices(numFingerprint);
            setPropDeviceDuplicates(propDuplicate);
            setLastRefreshed(Date.now());
        } catch (err) {
            console.log(err);
        }
    }

    const refreshData = async () => {
        setIsRefreshing(true);
        await getSurveyData();
        setIsRefreshing(false);
    }

    useEffect(() => {
        const getSurveyDataAndLoad = async () => {
            if (selectedSurveyId) {
                setIsLoadingSurvey(true);
                await getSurveyData();
                setIsLoadingSurvey(false);
            }
        }
        getSurveyDataAndLoad();
    }, [selectedSurveyId]);

    useEffect(() => {
        if (isSmall) {
            setUploadModalOpened(false);
            setQualtricsModalOpened(false);
        }
    }, [isSmall]);

    useEffect(() => {
        const getInitialSurveys = async () => {
            try {
                const APISurveys = await fetch("/.netlify/functions/check-for-loading-surveys", { method: "POST", body: JSON.stringify({ userId: currentUser.uid }) });
                const { surveysInProgress } = await APISurveys.json();
                setLoadingSurveys(surveysInProgress);
            } catch (err) {
                console.log(err);
            }
        }
        if (currentUser) {
            getInitialSurveys();
        }
    }, [currentUser]);

    useEffect(() => {
        if (typeof lastRefreshed === 'number') {
            const updateElapsedTime = () => {
                const currentTime = Date.now();
                const difference = currentTime - lastRefreshed;
                const minutes = Math.floor(difference / 60000);
                setElapsedMinutes(minutes);
            };
            updateElapsedTime();
            const intervalId = setInterval(updateElapsedTime, 60000);
            return () => clearInterval(intervalId);
        }
    }, [lastRefreshed]);

    // Effect to listen for window resize events
    useEffect(() => {
        const handleResize = () => { setWindowSize({ width: window.innerWidth, height: window.innerHeight }) };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const dashboardCardWidth = document.documentElement.clientWidth - (330 + getScrollbarWidth());

    return (
        <>
            <Helmet>
                <title>Dashboard | Roundtable</title>
                <meta
                    name='description'
                    content='View and manage your surveys run with the Roundtable API'
                />
            </Helmet>
            {
                isSmall ?
                    (
                        <div className={classes.smallScreenContainer}>
                            <Text>
                                The Alias Dashboard is not available on small screens.
                            </Text>
                        </div>
                    )
                    :
                    (
                        <div>
                            <AliasFileUpload
                                currentUser={currentUser}
                                uploadModalOpened={uploadModalOpened}
                                setUploadModalOpened={setUploadModalOpened}
                                APICreditsAvailable={APICreditsAvailable}
                                setAPICreditsAvailable={setAPICreditsAvailable}
                                surveyIds={aliasSurveyIds}
                                setSurveyIds={setAliasSurveyIds}
                                loadingSurveys={loadingSurveys}
                                setLoadingSurveys={setLoadingSurveys}
                            />
                            <QualtricsFileUpload
                                currentUser={currentUser}
                                qualtricsModalOpened={qualtricsModalOpened}
                                setQualtricsModalOpened={setQualtricsModalOpened}
                                APICreditsAvailable={APICreditsAvailable}
                                setAPICreditsAvailable={setAPICreditsAvailable}
                                surveyIds={aliasSurveyIds}
                                setSurveyIds={setAliasSurveyIds}
                                loadingSurveys={loadingSurveys}
                                setLoadingSurveys={setLoadingSurveys}
                            />
                            <div className={classes.toolbar}>
                                {
                                    isSmall === false ?
                                        <div className={classes.innerPlaygroundToolbarDiv}>
                                            <Menu styles={{ dropdown: { marginLeft: '20px' } }} >
                                                <Menu.Target>
                                                    <Tooltip
                                                        label="Call API on a new dataset"
                                                        color="dark"
                                                        position='right'
                                                    >
                                                        <ActionIcon
                                                            variant='transparent'
                                                            size={32}
                                                            sx={{
                                                                '&[data-disabled]': {
                                                                    background: 'none',
                                                                    border: 'none'
                                                                }
                                                            }}
                                                        >
                                                            <IconUpload
                                                                stroke={1.5}
                                                                size={20}
                                                                color='black'
                                                            />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                </Menu.Target>
                                                <Menu.Dropdown>
                                                    <Menu.Item
                                                        onClick={() => setUploadModalOpened(true)}
                                                    >
                                                        Upload raw dataset
                                                    </Menu.Item>
                                                    <Menu.Item
                                                        onClick={() => setQualtricsModalOpened(true)}
                                                    >
                                                        Upload Qualtrics dataset
                                                    </Menu.Item>
                                                </Menu.Dropdown>
                                            </Menu>
                                        </div>
                                        :
                                        <div />
                                }
                            </div>
                            <div className={classes.aliasWrapper}>
                                <div className={classes.surveyHistory}>
                                    <div className={classes.surveyTitleContainer}>
                                        <Space h='sm' />
                                        <Text tt='uppercase' weight={500} size='sm' className={classes.surveyHistoryTitle}>Your surveys</Text>
                                        <Space h='sm' />
                                    </div>
                                    {
                                        aliasSurveysLoaded === false ?
                                            <div className={classes.loaderListContainer}>
                                                <Loader size={22} className={classes.loader} />
                                            </div>
                                            :
                                            aliasSurveyIds.length > 0 ?
                                                <>
                                                    {
                                                        aliasSurveyIds.map((surveyId) => (
                                                            <div
                                                                key={surveyId}
                                                                onClick={() => setSelectedSurveyId(surveyId)}
                                                                className={cx(classes.surveyId, selectedSurveyId === surveyId && classes.surveyIdSelected)}
                                                            >
                                                                {

                                                                    (surveysProgress[surveyId] < 100 || loadingSurveys.includes(surveyId)) &&
                                                                    <>
                                                                        <RingProgress
                                                                            size={22}
                                                                            thickness={2.75}
                                                                            sections={[
                                                                                { value: surveysProgress[surveyId] > 0 ? surveysProgress[surveyId] : 0, color: 'teal' },
                                                                            ]}
                                                                            rootColor='#e3e3e3'
                                                                        />
                                                                        <Space w={5} />
                                                                    </>
                                                                }
                                                                <div className={classes.surveyIdText}>
                                                                    {surveyId}
                                                                </div>
                                                            </div>
                                                        ))
                                                    }
                                                </>
                                                :
                                                <div className={classes.noSurveysFound}>
                                                    <Text size='sm'>No surveys found...</Text>
                                                    <Space h='xs' />
                                                    <Text size='sm'>
                                                        <a
                                                            href='https://alias-docs.roundtable.ai'
                                                            target='_blank'
                                                            className={classes.linkText}
                                                        >Get started</a>
                                                    </Text>
                                                </div>
                                    }
                                </div>
                                <div className={classes.surveyVisualizer}>
                                    {
                                        isLoadingSurvey ?
                                            <div className={classes.loaderContainer}>
                                                <Loader size={30} />
                                            </div>
                                            :
                                            surveyData.length === 0
                                                ?
                                                <div className={classes.logoContainer}>
                                                    <LogoWithText innerWidth={500} innerHeight={500} opacity={0.35} logoScalingFactor={1.15} />
                                                </div>
                                                :
                                                <div style={{ width: `${dashboardCardWidth}px` }}>
                                                    <Card radius='lg' shadow='sm' p={isMedium ? 'lg' : 'xl'}>
                                                        <Card.Section inheritPadding withBorder py="md">
                                                            <Text weight={600} size='xl'>Survey statistics</Text>
                                                            <Group spacing={1}>
                                                                <Text color='dimmed' size='sm'>{getElapsedText(elapsedMinutes)}</Text>
                                                                <Tooltip
                                                                    label='Refresh data'
                                                                    position='right'
                                                                >
                                                                    <ActionIcon
                                                                        variant='transparent'
                                                                        onClick={refreshData}
                                                                        className={isRefreshing && classes.rotating}
                                                                        sx={{
                                                                            pointerEvents: isRefreshing ? 'none' : 'auto',
                                                                            cursor: isRefreshing ? 'not-allowed' : 'pointer',
                                                                            '&[data-disabled]': {
                                                                                background: 'none',
                                                                                border: 'none'
                                                                            }
                                                                        }}
                                                                    >
                                                                        <IconRefresh
                                                                            stroke={1.25}
                                                                            size={16}
                                                                            color={theme.colors.dimmed}
                                                                        />
                                                                    </ActionIcon>
                                                                </Tooltip>
                                                            </Group>
                                                        </Card.Section>
                                                        <Space h='lg' />
                                                        <Group align='flex-start' spacing={isXl ? 155 : isMedium ? 50 : 75} noWrap style={{ maxWidth: '1500px' }}>
                                                            <Group position='center' spacing={10} style={{ flexGrow: '1', width: '10px' }}>
                                                                <Group>
                                                                    <Text weight={600}>Summary</Text>
                                                                    <Text className={classes.flexText}>
                                                                        {isMedium ? 'Num' : 'Number of'} participants <Text component='span' className={classes.flexTextSpan}>{surveyData.length}</Text>
                                                                    </Text>
                                                                    <Text className={classes.flexText}>
                                                                        {isMedium ? 'Num open-ends' : 'Open-ends analyzed'} <Text component='span' className={classes.flexTextSpan}>{numQuestionsAnswered}</Text>
                                                                    </Text>
                                                                    {
                                                                        numFingerprintDevices > 0 &&
                                                                        <>
                                                                            <Text className={classes.flexText}>
                                                                                Devices analyzed<Text component='span' className={classes.flexTextSpan}>{numFingerprintDevices}</Text>
                                                                            </Text>
                                                                            <Text className={classes.flexText}>
                                                                                Duplicate devices<Text component='span' className={classes.flexTextSpan}>
                                                                                { numFingerprintDevices === 1 ? 'N/A' : `${getFlagCountText(propDeviceDuplicates)}%` }
                                                                            </Text>
                                                                            </Text>
                                                                        </>
                                                                    }
                                                                    <Text className={classes.flexText}>
                                                                        API model
                                                                        <Text component='span' className={classes.flexTextSpan}>{aliasModel}</Text>
                                                                    </Text>
                                                                </Group>
                                                                <Group position='center' style={{ width: '250px' }} spacing={0}>
                                                                    <RingProgress
                                                                        size={isMedium ? 200 : 225}
                                                                        thickness={isMedium ? 24 : 25}
                                                                        sections={[{ value: flagCounts['None'] ? 100 - flagCounts['None'] : 100, color: '#d98484' }]}
                                                                        label={<Text
                                                                            color='#d98484'
                                                                            weight={600}
                                                                            align="center"
                                                                            size={isMedium ? 'lg' : 'xl'}
                                                                        >
                                                                            {flagCounts['None'] ? getProportionFlagged(flagCounts['None']) : 100}%<br />flagged
                                                                        </Text>
                                                                        }
                                                                    />
                                                                </Group>
                                                            </Group>
                                                            <Group style={{ flexGrow: '1', width: '10px' }}>
                                                                <Text weight={600}>Open-end flags (all responses)</Text>
                                                                {
                                                                    Object.keys(flagCounts).map((flag) => (
                                                                        flag !== 'None' &&
                                                                        <Group key={flag} position='apart' style={{ width: '100%' }}>
                                                                            <Text className={classes.flexText}>
                                                                                {isMedium ? shortenedFlags[flag] ? shortenedFlags[flag] : flag : flag}
                                                                                <Text component='span' className={classes.flexTextSpan}>
                                                                                    {getFlagCountText(flagCounts[flag])}%
                                                                                </Text>
                                                                            </Text>
                                                                        </Group>
                                                                    ))
                                                                }
                                                            </Group>
                                                        </Group>
                                                        <Space h='xs' />
                                                    </Card>
                                                    <Card radius='lg' shadow='sm' p={isMedium ? 'lg' : 'xl'} mt='35px'>
                                                        <Card.Section inheritPadding withBorder py="md">
                                                            <Group position='apart'>
                                                                <div>
                                                                    <Text weight={600} size='xl'>Survey data</Text>
                                                                    <Group spacing={1}>
                                                                        <Text color='dimmed' size='sm'>{getElapsedText(elapsedMinutes)}</Text>
                                                                        <Tooltip
                                                                            label='Refresh data'
                                                                            position='right'
                                                                        >
                                                                            <ActionIcon
                                                                                variant='transparent'
                                                                                onClick={refreshData}
                                                                                className={isRefreshing && classes.rotating}
                                                                                sx={{
                                                                                    pointerEvents: isRefreshing ? 'none' : 'auto',
                                                                                    cursor: isRefreshing ? 'not-allowed' : 'pointer',
                                                                                    '&[data-disabled]': {
                                                                                        background: 'none',
                                                                                        border: 'none'
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <IconRefresh
                                                                                    stroke={1.25}
                                                                                    size={16}
                                                                                    color={theme.colors.dimmed}
                                                                                />
                                                                            </ActionIcon>
                                                                        </Tooltip>
                                                                    </Group>
                                                                </div>
                                                                <Tooltip
                                                                    label='Download CSV'
                                                                    color="dark"
                                                                    position='left'
                                                                >
                                                                    <ActionIcon
                                                                        variant='transparent'
                                                                        size={32}
                                                                        disabled={isDownloading}
                                                                        sx={{
                                                                            '&[data-disabled]': {
                                                                                background: 'none',
                                                                                border: 'none'
                                                                            }
                                                                        }}
                                                                    >
                                                                        {isDownloading
                                                                            ?
                                                                            <Loader size={20} />
                                                                            :
                                                                            <IconDownload
                                                                                stroke={1.5}
                                                                                size={20}
                                                                                color='black'
                                                                                onClick={async () => {
                                                                                    setIsDownloading(true);
                                                                                    await downloadCSV(surveyData, `survey-${selectedSurveyId}.csv`)
                                                                                    setIsDownloading(false);
                                                                                }}
                                                                                className={cx(classes.toolbarIcon, isLoadingSurvey && classes.toolbarItemDisabled)}
                                                                            />
                                                                        }
                                                                    </ActionIcon>
                                                                </Tooltip>

                                                            </Group>
                                                        </Card.Section>
                                                        <div className={classes.tableContainer}>
                                                            <TableSorting
                                                                rowNames={rowNames}
                                                                surveyData={surveyData}
                                                            />
                                                        </div>
                                                    </Card>
                                                </div>
                                    }
                                </div>
                            </div>
                        </div>
                    )
            }
        </>
    )
};

export default Dashboard;